import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * In-app confirmation dialog, replacing browser window.confirm() popups which
 * look like debug tooling and ignore the product theme.
 */

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(5, 5, 12, 0.75);
  backdrop-filter: blur(3px);
  display: grid;
  place-items: center;
  z-index: 1000;
`;

const Dialog = styled(motion.div)`
  background: rgba(22, 26, 43, 0.98);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 1.75rem;
  width: min(92vw, 420px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const DialogTitle = styled.h3`
  margin: 0 0 0.6rem;
  color: var(--text-primary);
  font-size: 1.15rem;
`;

const DialogMessage = styled.p`
  margin: 0 0 1.4rem;
  color: var(--text-secondary);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const DialogActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const DialogButton = styled.button`
  border: none;
  padding: 0.65rem 1.3rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  background: ${(p) => (p.$variant === 'danger'
    ? 'rgba(248, 113, 113, 0.15)'
    : p.$variant === 'ghost'
      ? 'transparent'
      : 'var(--accent-cyan)')};
  color: ${(p) => (p.$variant === 'danger'
    ? '#f87171'
    : p.$variant === 'ghost'
      ? 'var(--text-secondary)'
      : 'var(--primary-bg)')};
  border: ${(p) => (p.$variant === 'danger'
    ? '1px solid rgba(248, 113, 113,0.4)'
    : p.$variant === 'ghost'
      ? '1px solid var(--border-color)'
      : 'none')};
`;

const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  onConfirm,
  onCancel
}) => (
  <AnimatePresence>
    {open && (
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onCancel}
      >
        <Dialog
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          onClick={(e) => e.stopPropagation()}
        >
          <DialogTitle>{title}</DialogTitle>
          <DialogMessage>{message}</DialogMessage>
          <DialogActions>
            <DialogButton type="button" $variant="ghost" onClick={onCancel}>
              {cancelLabel}
            </DialogButton>
            <DialogButton
              type="button"
              $variant={danger ? 'danger' : 'primary'}
              onClick={onConfirm}
            >
              {confirmLabel}
            </DialogButton>
          </DialogActions>
        </Dialog>
      </Overlay>
    )}
  </AnimatePresence>
);

export default ConfirmDialog;
